import { useTheme } from "next-themes";
import { Toaster as Sonner, toast as sonnerToast } from "sonner";

type ToasterProps = React.ComponentProps<typeof Sonner>;

// Haptic feedback patterns (vibration duration in ms)
const HAPTIC_PATTERNS = {
  success: [10], // Short tap
  error: [50, 30, 50], // Double tap
  warning: [30, 20, 30], // Medium double tap
  info: [10], // Short tap
  default: [10], // Short tap
};

// Trigger haptic feedback if supported
const triggerHaptic = (type: keyof typeof HAPTIC_PATTERNS = "default") => {
  if (typeof navigator !== "undefined" && "vibrate" in navigator) {
    try {
      navigator.vibrate(HAPTIC_PATTERNS[type] || HAPTIC_PATTERNS.default);
    } catch (e) {
      // Silently fail if vibration not supported
    }
  }
};

// Wrapped toast functions with haptic feedback
const toast = Object.assign(
  (message: string | React.ReactNode, data?: Parameters<typeof sonnerToast>[1]) => {
    triggerHaptic("default");
    return sonnerToast(message, data);
  },
  {
    success: (message: string | React.ReactNode, data?: Parameters<typeof sonnerToast.success>[1]) => {
      triggerHaptic("success");
      return sonnerToast.success(message, data);
    },
    error: (message: string | React.ReactNode, data?: Parameters<typeof sonnerToast.error>[1]) => {
      triggerHaptic("error");
      return sonnerToast.error(message, data);
    },
    warning: (message: string | React.ReactNode, data?: Parameters<typeof sonnerToast.warning>[1]) => {
      triggerHaptic("warning");
      return sonnerToast.warning(message, data);
    },
    info: (message: string | React.ReactNode, data?: Parameters<typeof sonnerToast.info>[1]) => {
      triggerHaptic("info");
      return sonnerToast.info(message, data);
    },
    loading: sonnerToast.loading,
    promise: sonnerToast.promise,
    dismiss: sonnerToast.dismiss,
    custom: sonnerToast.custom,
    message: sonnerToast.message,
  }
);

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      position="top-center"
      expand={false}
      closeButton
      toastOptions={{
        classNames: {
          // pointer-events-auto: a toast must stay clickable even when a Radix
          // modal (detail sheet / dialog) is open — those set body{pointer-events:none},
          // which otherwise makes the toast visible-but-dead (clicks fall through
          // to the sheet behind it). The toaster region stays none so empty space
          // never blocks the page.
          toast:
            "group toast pointer-events-auto group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg",
          description: "group-[.toast]:text-muted-foreground",
          actionButton: "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton: "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
        },
      }}
      {...props}
    />
  );
};

export { Toaster, toast };
